import React, { useState } from 'react'
import EditIcon from '@material-ui/icons/Edit'
import DownloadIcon from '@material-ui/icons/Archive'

import moment from 'config/moment'
import { ERRORS, STRINGS } from 'consts'
import { useApi, useApiCall } from 'hooks'
import { snackbar } from 'components'
import { createDateSort } from 'utils'

import { getVouchers, getVoucherUsers } from './api'
import { DefaultTable, TagsIndicator } from '../components'
import { GetVouchersResponse, Voucher, GetVoucherUsersResponse } from './types'
import { CreateVoucherDialog, DeleteVoucherDialog, UpdateVoucherDialog } from './components'
import { getActiveTags } from '../Tags/api'
import { GetTagsResponse, Tag } from '../Tags/types'
import { downloadCSV, formatManualRewardUserCSV } from '../Downloads/utils/parseCsv'

const isEcardEnabled = process.env.REACT_APP_ECARD_ENABLED === 'true'

const columns = [
  { title: 'ID', field: 'id' },
  {
    title: 'Título',
    field: 'listingTitle',
    render: (row: Voucher) => row.listingTitle || row.title,
  },
  { title: 'Descripción', field: 'shortTitle' },
  ...(!isEcardEnabled
    ? [
        {
          title: 'Icono',
          render: (rowData: Voucher) => <img src={rowData.iconUrl} alt="" style={{ width: 20 }} />,
          sorting: false,
        },
      ]
    : []),
  {
    title: 'Tags',
    render: (rowData: Voucher) => <TagsIndicator tags={rowData.tags} />,
    sorting: false,
  },
  {
    title: 'Emails precargados',
    render: (rowData: Voucher) => rowData.preloadedEmails?.length || 0,
    sorting: false,
  },
  {
    title: 'Inicio',
    field: 'startDate',
    render: (rowData: Voucher) =>
      rowData.startDate ? moment(rowData.startDate).format(STRINGS.DATE_FORMAT) : '-',
    customSort: createDateSort<Voucher>(row => row.startDate),
  },
  {
    title: 'Vencimiento',
    field: 'expiration',
    render: (rowData: Voucher) => (
      <span
        style={{
          color: moment()
            .startOf('day')
            .isAfter(rowData.expiration)
            ? 'red'
            : 'black',
        }}
      >
        {moment(rowData.expiration).format(STRINGS.DATE_FORMAT)}
      </span>
    ),
    customSort: createDateSort<Voucher>(row => row.expiration),
  },
  {
    title: 'Activo',
    render: (rowData: Voucher) => (rowData.active ? 'Sí' : 'No'),
    sorting: false,
  },
]

const getVouchersResponseGetter = (responseData: GetVouchersResponse) =>
  responseData?.vouchers ?? []
const getTagsResponseGetter = (responseData: GetTagsResponse) => responseData?.tags ?? []

const VouchersList: React.FC = () => {
  const [{ data: vouchers, isLoading }, , setVouchers] = useApi<GetVouchersResponse, Voucher[]>(
    getVouchers,
    getVouchersResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )
  const [{ data: tags }] = useApi<GetTagsResponse, Tag[]>(getActiveTags, getTagsResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })

  const [getVoucherUsersApi] = useApiCall<number, GetVoucherUsersResponse>(getVoucherUsers)

  const [isCreateVoucherDialogOpen, setIsCreateVoucherDialogOpen] = useState(false)
  const [isDeleteVoucherDialogOpen, setIsDeleteVoucherDialogOpen] = useState(false)
  const [voucherToDelete, setVoucherToDelete] = useState<Voucher | null>(null)
  const [isUpdateVoucherDialogOpen, setIsUpdateVoucherDialogOpen] = useState(false)
  const [voucherToUpdate, setVoucherToUpdate] = useState<Voucher | null>(null)

  const handleCreateVoucherDone = (newVoucher: Voucher) => {
    setVouchers([newVoucher, ...vouchers])
    setIsCreateVoucherDialogOpen(false)
    snackbar.show('Voucher creado. La asignación se procesará en segundo plano.')
  }

  const handleDeleteVoucherDialogClose = () => {
    setIsDeleteVoucherDialogOpen(false)
    setVoucherToDelete(null)
  }

  const handleDeleteVoucherDone = () => {
    if (!voucherToDelete) return
    setVouchers(vouchers.filter(voucher => voucher.id !== voucherToDelete.id))
    handleDeleteVoucherDialogClose()
  }

  const handleUpdateVoucherDialogClose = () => {
    setIsUpdateVoucherDialogOpen(false)
    setVoucherToUpdate(null)
  }

  const handleUpdateVoucherDone = (updatedVoucher: Voucher) => {
    setVouchers(
      vouchers.map(voucher => (voucher.id === updatedVoucher.id ? updatedVoucher : voucher)),
    )
    handleUpdateVoucherDialogClose()
    snackbar.show('Voucher actualizado. La asignación se sincronizará en segundo plano.')
  }

  const handleDownloadUsers = async (voucher: Voucher) => {
    const { voucherUsers } = await getVoucherUsersApi(voucher.id)
    if (voucherUsers.length) {
      downloadCSV(formatManualRewardUserCSV(voucherUsers), `voucher-${voucher.id}-usuarios`)
    } else {
      snackbar.show(ERRORS.NO_DATA_ERROR_MESSAGE)
    }
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Crear nuevo voucher',
      isFreeAction: true,
      onClick: () => setIsCreateVoucherDialogOpen(true),
    },
    {
      icon: () => <DownloadIcon />,
      tooltip: 'Descargar usuarios asignados',
      isFreeAction: false,
      onClick: (_: React.SyntheticEvent, row: Voucher | Voucher[]) =>
        handleDownloadUsers(row as Voucher),
    },
    {
      icon: () => <EditIcon />,
      tooltip: 'Editar voucher',
      onClick: (_: React.SyntheticEvent, row: Voucher | Voucher[]) => {
        setVoucherToUpdate(row as Voucher)
        setIsUpdateVoucherDialogOpen(true)
      },
    },
    {
      icon: 'delete',
      tooltip: 'Eliminar',
      onClick: (_: React.SyntheticEvent, row: Voucher | Voucher[]) => {
        setVoucherToDelete(row as Voucher)
        setIsDeleteVoucherDialogOpen(true)
      },
    },
  ]

  return (
    <>
      <DefaultTable
        title=""
        columns={columns}
        data={vouchers}
        isLoading={isLoading}
        actions={actions}
      />
      {isCreateVoucherDialogOpen && tags && (
        <CreateVoucherDialog
          handleClose={() => setIsCreateVoucherDialogOpen(false)}
          onDone={handleCreateVoucherDone}
          tags={tags}
        />
      )}
      {isDeleteVoucherDialogOpen && voucherToDelete && (
        <DeleteVoucherDialog
          handleClose={handleDeleteVoucherDialogClose}
          voucherId={voucherToDelete.id}
          voucherTitle={voucherToDelete.listingTitle || voucherToDelete.title}
          onDone={handleDeleteVoucherDone}
        />
      )}
      {isUpdateVoucherDialogOpen && voucherToUpdate && (
        <UpdateVoucherDialog
          handleClose={handleUpdateVoucherDialogClose}
          voucherToUpdate={voucherToUpdate}
          onDone={handleUpdateVoucherDone}
          tags={tags}
        />
      )}
    </>
  )
}

export { VouchersList }
